// ---------------------------------------------------------
// 1. Enums & Config
// ---------------------------------------------------------

export const GEMINI_MODEL = {
  FLASH: 'gemini-2.5-flash',
};

// ---------------------------------------------------------
// 2. Categories
// ---------------------------------------------------------

export const CATEGORIES = [
  {
    id: 'cat_marketing',
    name: 'Digital Marketing',
    iconName: 'Megaphone', 
    description: 'Social media, SEO, content marketing, and advertising services',
  },
  {
    id: 'cat_ai',
    name: 'AI Services',
    iconName: 'Bot', 
    description: 'AI integration, chatbots, machine learning, and automation',
  },
  {
    id: 'cat_design',
    name: 'Graphic Design',
    iconName: 'Palette', 
    description: 'Logos, branding, illustrations, and visual design',
  },
  {
    id: 'cat_dev',
    name: 'Technology',
    iconName: 'Code', 
    description: 'Web development, mobile apps, and software solutions',
  },
  {
    id: 'cat_virtual',
    name: 'Virtual Assistant',
    iconName: 'Headset', 
    description: 'Administrative support, scheduling, and customer service',
  },
  {
    id: 'cat_automation',
    name: 'Automation',
    iconName: 'Cpu', 
    description: 'Workflow automation, integrations, and process optimization',
  },
  {
    id: 'cat_ecommerce',
    name: 'E-Commerce',
    iconName: 'ShoppingBag', 
    description: 'Online store setup, product listings, and marketplace management',
  },
  {
    id: 'cat_education',
    name: 'Education',
    iconName: 'GraduationCap', 
    description: 'Tutoring, course creation, and educational content',
  },
  {
    id: 'cat_finance',
    name: 'Finance',
    iconName: 'BadgeDollarSign', 
    description: 'Bookkeeping, financial planning, and accounting services',
  },
  {
    id: 'cat_legal',
    name: 'Legal Services',
    iconName: 'Scale', 
    description: 'Contract review, legal documents, and compliance',
  },
  {
    id: 'cat_security',
    name: 'Cybersecurity',
    iconName: 'ShieldCheck', 
    description: 'Security audits, penetration testing, and data protection',
  },
  {
    id: 'cat_writing',
    name: 'Writing & Translation',
    iconName: 'FileText', 
    description: 'Copywriting, content creation, and translation services',
  },
  {
    id: 'cat_business',
    name: 'Business Consulting',
    iconName: 'Briefcase', 
    description: 'Strategy, operations, and business development',
  },
  {
    id: 'cat_lifestyle',
    name: 'Lifestyle',
    iconName: 'UserCircle', 
    description: 'Personal coaching, wellness, and lifestyle services',
  },
];

// Helper function to get category by ID
export const getCategoryById = (categoryId) => {
  return CATEGORIES.find(c => c.id === categoryId);
};

// Helper function to get category name by ID
export const getCategoryName = (categoryId) => {
  return CATEGORIES.find(c => c.id === categoryId)?.name || 'Other';
};

// ---------------------------------------------------------
// 3. Portfolio Items (for demo data)
// ---------------------------------------------------------

export const PORTFOLIO_ITEMS = [
  {
    id: 'item_1',
    title: 'E-commerce Redesign',
    imageUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=500',
    linkUrl: 'https://example.com/project1',
    category: 'Technology',
  },
  {
    id: 'item_2',
    title: 'Travel App UI',
    imageUrl: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=500',
    linkUrl: 'https://example.com/project2',
    category: 'Graphic Design',
  },
  {
    id: 'item_3',
    title: 'AI Chatbot Integration',
    imageUrl: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=500',
    linkUrl: 'https://example.com/project3',
    category: 'AI Services',
  },
];

// ---------------------------------------------------------
// 4. Demo Providers (for initial testing without Firestore)
// ---------------------------------------------------------

export const DEMO_PROVIDERS = [
  {
    id: 'prov_1',
    name: 'Sarah Jenkins',
    avatarUrl: 'https://i.pravatar.cc/150?u=sarah',
    coverUrl: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&w=800',
    bio: 'Senior Full Stack Developer with a passion for scalable React applications and Node.js backends.',
    location: 'San Francisco, CA',
    joinedDate: '2022-03-15',
    isVerified: true,
    rating: 4.9,
    reviewCount: 142,
    jobsCompleted: 89,
    badges: ['Top Rated', 'Fast Delivery'],
    categories: ['Technology', 'AI Services'],
    portfolioItems: [PORTFOLIO_ITEMS[0], PORTFOLIO_ITEMS[2]],
    yearsExperience: 7,
    certifications: ['AWS Certified Solutions Architect', 'Meta Frontend Developer'],
    languages: ['English', 'Spanish'],
    responseTime: '1 hour',
    portfolioUrl: 'https://sarahjenkins.dev',
  },
  {
    id: 'prov_2',
    name: 'David Chen',
    avatarUrl: 'https://i.pravatar.cc/150?u=david',
    coverUrl: 'https://images.unsplash.com/photo-1557683316-973673baf926?auto=format&fit=crop&w=800',
    bio: 'Creative graphic designer specializing in brand identity and logo design.',
    location: 'Toronto, Canada',
    joinedDate: '2023-01-10',
    isVerified: false,
    rating: 4.7,
    reviewCount: 56,
    jobsCompleted: 30,
    badges: ['Rising Talent'],
    categories: ['Graphic Design'],
    portfolioItems: [PORTFOLIO_ITEMS[1]],
    yearsExperience: 3,
    certifications: ['Adobe Certified Expert'],
    languages: ['English', 'Mandarin'],
    responseTime: '4 hours',
  },
  {
    id: 'prov_3',
    name: 'Elena Rodriguez',
    avatarUrl: 'https://i.pravatar.cc/150?u=elena',
    coverUrl: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800',
    bio: 'Digital marketing strategist helping brands grow their online presence.',
    location: 'Austin, TX',
    joinedDate: '2023-05-20',
    isVerified: true,
    rating: 4.8,
    reviewCount: 92,
    jobsCompleted: 65,
    badges: ['Marketing Pro'],
    categories: ['Digital Marketing', 'E-Commerce'],
    portfolioItems: [],
    yearsExperience: 5,
    certifications: ['Google Ads Certified', 'HubSpot Content Marketing'],
    languages: ['English', 'Portuguese'],
    responseTime: '2 hours',
  },
];

// ---------------------------------------------------------
// 5. Demo Services (for initial testing without Firestore)
// ---------------------------------------------------------

export const SERVICES = [
  {
    id: 'serv_1',
    providerId: 'prov_1',
    title: 'Full Stack React & Node Application',
    description: 'I will build a complete, scalable web application using the MERN stack with authentication, database integration, and deployment.',
    categoryId: 'cat_dev',
    category: 'Technology',
    priceStart: 1200,
    rating: 4.9,
    reviews: 142,
    imageUrl: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=500',
    tags: ['React', 'Node.js', 'MongoDB', 'API', 'Full Stack'],
    status: 'published',
  },
  {
    id: 'serv_2',
    providerId: 'prov_2',
    title: 'Minimalist Logo Design',
    description: 'A professional, vector-based minimalist logo for your business including multiple concepts and revisions.',
    categoryId: 'cat_design',
    category: 'Graphic Design',
    priceStart: 150,
    rating: 4.7,
    reviews: 56,
    imageUrl: 'https://images.unsplash.com/photo-1626785774573-4b799314346d?auto=format&fit=crop&w=500',
    tags: ['Logo', 'Branding', 'Vector', 'Minimalist'],
    status: 'published',
  },
  {
    id: 'serv_3',
    providerId: 'prov_3',
    title: 'Social Media Growth Package',
    description: 'Comprehensive management of your Instagram and LinkedIn profiles including content creation, scheduling, and analytics.',
    categoryId: 'cat_marketing',
    category: 'Digital Marketing',
    priceStart: 800,
    rating: 4.8,
    reviews: 92,
    imageUrl: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&w=500',
    tags: ['Instagram', 'LinkedIn', 'Growth', 'Social Media'],
    status: 'published',
  },
  {
    id: 'serv_4',
    providerId: 'prov_1',
    title: 'Custom AI Chatbot Integration',
    description: 'Integrate OpenAI or Gemini models into your existing website with custom training and deployment.',
    categoryId: 'cat_ai',
    category: 'AI Services',
    priceStart: 1500,
    rating: 5.0,
    reviews: 12,
    imageUrl: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=500',
    tags: ['AI', 'Chatbot', 'LLM', 'OpenAI', 'Integration'],
    status: 'published',
  },
  {
    id: 'serv_5',
    providerId: 'prov_2',
    title: 'Complete Brand Identity Package',
    description: 'Full branding solution including logo, color palette, typography, business cards, and brand guidelines.',
    categoryId: 'cat_design',
    category: 'Graphic Design',
    priceStart: 500,
    rating: 4.8,
    reviews: 34,
    imageUrl: 'https://images.unsplash.com/photo-1558655146-9f40138edfeb?auto=format&fit=crop&w=500',
    tags: ['Branding', 'Identity', 'Logo', 'Guidelines'],
    status: 'published',
  },
  {
    id: 'serv_6',
    providerId: 'prov_3',
    title: 'SEO Optimization & Audit',
    description: 'Comprehensive SEO audit with actionable recommendations to improve your search rankings and organic traffic.',
    categoryId: 'cat_marketing',
    category: 'Digital Marketing',
    priceStart: 400,
    rating: 4.6,
    reviews: 28,
    imageUrl: 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?auto=format&fit=crop&w=500',
    tags: ['SEO', 'Audit', 'Analytics', 'Optimization'],
    status: 'published',
  },
];

// ---------------------------------------------------------
// 6. Service Status Enum
// ---------------------------------------------------------

export const ServiceStatus = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
};

// ---------------------------------------------------------
// 7. Booking Status Enum
// ---------------------------------------------------------

export const BookingStatus = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  DISPUTED: 'disputed',
};